import { COMPANY_CARD_SECTIONS } from "@/lib/company-card-sections";
import type { CompanySectionKey } from "@shaarp/common";
import type { QueryKey } from "@tanstack/react-query";

/**
 * The cache options of a tab read, shared by the prefetch and the tab so both
 * treat the entry alike. It stays fresh long enough for the tab, opened some
 * time after the pointer passed over it, to read it; a refusal (the team's
 * daily tab reads) is not retried.
 */
export const COMPANY_TAB_READ_QUERY_OPTIONS = {
  staleTime: 5 * 60_000,
  retry: false,
} as const;

/** One read of a company tab: its cache key, its route, the sections it returns the data of. */
export type CompanyTabRead = {
  readonly key: QueryKey;
  readonly path: string;
  readonly sections: readonly CompanySectionKey[];
};

/**
 * The reads of the news tab, shared by the tab (`useCompanyNewsData`) and the
 * page that prefetches them, so both hit the same cache entry. Keyed by team:
 * each read counts against the team's daily tab reads.
 */
export function companyTabReads(teamId: string, companyId: string) {
  return {
    socialActivities: {
      key: ["company", teamId, companyId, "social-activities"],
      path: `/company/${companyId}/social-networks/activities`,
      sections: COMPANY_CARD_SECTIONS.posts,
    },
    reviews: {
      key: ["company", teamId, companyId, "reviews"],
      path: `/company/${companyId}/reviews`,
      sections: COMPANY_CARD_SECTIONS.reviews,
    },
    jobOffers: {
      key: ["company", teamId, companyId, "job-offers"],
      path: `/company/${companyId}/job-offers`,
      sections: COMPANY_CARD_SECTIONS.jobs,
    },
  } as const satisfies Record<string, CompanyTabRead>;
}
